<template>
  <ion-page>
    <main-header :back-href="reportsHref">
      <ion-toolbar>
        <ion-grid>
          <ion-row>
            <ion-col>
              <ion-grid class="ion-no-padding">
                <ion-row>
                  <ion-col class="ion-no-padding" size="5">
                    <ion-datetime
                      display-format="MM/DD/YYYY"
                      picker-format="MMM D, YYYY"
                      :value="startDateFilter"
                      @ionChange="handleFilteredStartDateAndUpdate"
                    ></ion-datetime>
                  </ion-col>
                  <ion-col
                    class="ion-text-center ion-standard-font ion-padding-top-sm"
                    size="2"
                  >
                    to
                  </ion-col>
                  <ion-col class="ion-no-padding" size="5">
                    <ion-datetime
                      display-format="MM/DD/YYYY"
                      picker-format="MMM D, YYYY"
                      :value="endDateFilter"
                      @ionChange="handleFilteredEndDateAndUpdate"
                    ></ion-datetime>
                  </ion-col>
                </ion-row>
              </ion-grid>
            </ion-col>
            <ion-col>
              <ion-row>
                <ion-col class="ion-no-padding" size="9">
                  <ion-select
                    placeholder="Location"
                    value
                    @ionChange="handleFilteredLocationAndUpdate"
                  >
                    <ion-select-option value>All Locations</ion-select-option>
                    <ion-select-option
                      v-for="location in locations"
                      v-bind:key="`location-${location.id}`"
                      :value="location.id"
                    >
                      {{ location.name }}
                    </ion-select-option>
                  </ion-select>
                </ion-col>
                <ion-col class="ion-no-padding ion-text-end" size="3">
                  <ion-button @click="populateTransactions()">
                    Update
                  </ion-button>
                </ion-col>
              </ion-row>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-toolbar>
    </main-header>
    <ion-content padding>
      <loading-spinner v-if="loading"></loading-spinner>
      <div v-else>
        <div
          v-if="!transactions.length"
          class="background-color ion-color-warning ion-padding ion-text-center"
        >
          There are no transactions available.
        </div>
        <div v-else>
          <ion-grid class="order-table">
            <ion-row class="header">
              <ion-col size="1">
                Transaction ID
              </ion-col>
              <ion-col v-if="userIsAdmin" size="2">
                Customer
              </ion-col>
              <ion-col size="2">
                Location
              </ion-col>
              <ion-col size="1">
                Item Total
              </ion-col>
              <ion-col size="1">
                Service Charge
              </ion-col>
              <ion-col size="1">
                Order Total
              </ion-col>
              <ion-col size="1">
                Fulfillment
              </ion-col>
              <ion-col :size="userIsAdmin ? 1 : 5">
                Date/Time
              </ion-col>
              <ion-col v-if="userIsAdmin" size="2"> </ion-col>
            </ion-row>
            <ion-row
              v-for="transaction in transactions"
              :key="`transaction-${transaction.id}`"
            >
              <ion-col size="1">
                <small>{{ transaction.id }}</small>
              </ion-col>
              <ion-col v-if="userIsAdmin" size="2">
                <router-link
                  v-if="transaction.userId"
                  :to="{
                    name: 'Web: User Edit',
                    params: { uid: transaction.userId }
                  }"
                >
                  {{ transaction.displayName || "None" }}
                </router-link>
                <span v-else>
                  Unknown
                </span>
              </ion-col>
              <ion-col size="2">
                {{ transaction.locationName }}
              </ion-col>
              <ion-col size="1"> ${{ transaction.itemTotal }} </ion-col>
              <ion-col size="1"> ${{ transaction.serviceCharge }} </ion-col>
              <ion-col size="1"> ${{ transaction.total }} </ion-col>
              <ion-col size="1">
                {{
                  transaction.fulfillment === ORDER_FULFILLMENT_PICKUP
                    ? "Pick-up"
                    : "Delivery"
                }}
              </ion-col>
              <ion-col :size="userIsAdmin ? 1 : 5">
                {{ transaction.created }}
              </ion-col>
              <ion-col v-if="userIsAdmin" size="2" class="ion-text-center">
                <ion-button @click="goToTransactionDetail(transaction.id)">
                  Details
                </ion-button>
              </ion-col>
            </ion-row>
          </ion-grid>
          <hr />
          <div class="ion-text-end">
            <ion-button @click="download()">
              Download
              <ion-icon slot="end" name="cloud-download"></ion-icon>
            </ion-button>
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import { mapGetters } from "vuex";
import Papa from "papaparse";
import LocationModelService from "@/services/LocationModel";
import errorAlert from "@/ErrorAlert";
import { centsToDollars } from "@/util";
import { ORDER_FULFILLMENT_PICKUP } from "@/constants/order";

export default {
  title: "Transactions",
  props: {
    startDate: {
      required: false,
      type: String
    }
  },

  data: function() {
    return {
      ORDER_FULFILLMENT_PICKUP,
      loading: true,
      transactions: [],
      locations: [],
      locationFilter: "",
      startDateFilter: "",
      endDateFilter: ""
    };
  },

  created() {
    if (!this.userIsAdmin && !this.userIsVendor) {
      return this.$router.push("/").catch(e => e);
    }

    this.setInitialDates();

    this.populateLocations();

    this.populateTransactions();
  },

  methods: {
    handleFilteredStartDateAndUpdate(event) {
      this.startDateFilter = event.detail.value;
    },

    handleFilteredEndDateAndUpdate(event) {
      this.endDateFilter = event.detail.value;
    },

    handleFilteredLocationAndUpdate(event) {
      this.locationFilter = event.detail.value;
    },

    setInitialDates() {
      let now;

      if (this.startDate && /^\d{4}-\d{2}-\d{2}$/.test(this.startDate)) {
        let parts = this.startDate.split("-");
        now = new Date(
          parseInt(parts[0]),
          parseInt(parts[1]) - 1,
          parseInt(parts[2])
        );
      }

      now = now || new Date();

      this.startDateFilter = new Date(
        now.getFullYear(),
        now.getMonth(),
        1
      ).toDateString();
      this.endDateFilter = new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        0
      ).toDateString();
    },

    userDetailHref(uid) {
      return this.$router.resolve({
        name: "Web: User Edit",
        params: {
          uid
        }
      }).href;
    },

    async populateLocations() {
      let q = LocationModelService.currentUserLocations();
      q.then(locations => {
        this.locations = locations.data;
      });
    },

    populateTransactions() {
      this.loading = true;
      this.transactions = [];

      let transactionsReport = this.$functions().httpsCallable(
        "transactionsReport"
      );

      transactionsReport({
        locationId: this.locationFilter,
        startDate: new Date(this.startDateFilter).toISOString().split("T")[0],
        endDate: new Date(this.endDateFilter).toISOString().split("T")[0],
        timezoneOffset: new Date().getTimezoneOffset()
      })
        .then(results => {
          this.transactions = results.data.map(data => {
            return {
              ...data,
              itemTotal: centsToDollars(data.itemTotal),
              serviceCharge: centsToDollars(data.serviceCharge),
              total: centsToDollars(data.total) // done for reporting
            };
          });
        })
        .catch(e => {
          errorAlert(e.message);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    download() {
      let csv = Papa.unparse(
        this.transactions.map(transaction => {
          let row = {
            "Transaction ID": transaction.id,
            Location: transaction.locationName,
            "Item Total": transaction.itemTotal,
            "Service Charge": transaction.serviceCharge,
            Total: transaction.total,
            Fulfillment:
              transaction.fulfillment === ORDER_FULFILLMENT_PICKUP
                ? "Pick-up"
                : "Delivery",
            "Date/Time": transaction.created
          };

          if (this.userIsAdmin) {
            row["Customer ID"] = transaction.userId;
            row["Customer Name"] = transaction.displayName;
          }

          return row;
        })
      );

      let blob = new Blob([csv]);
      let a = window.document.createElement("a");
      a.href = window.URL.createObjectURL(blob, { type: "text/csv" });
      a.download = "transaction-report.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },

    goToTransactionDetail(transactionId) {
      this.$router
        .push({
          name: "Web: Transaction Detail",
          params: {
            id: transactionId
          }
        })
        .catch(e => e);
    }
  },

  computed: {
    reportsHref() {
      return this.$router.resolve({
        name: "Web: Reports"
      }).href;
    },

    ...mapGetters("auth", ["userIsAdmin", "userIsVendor"])
  }
};
</script>
